import React, { useState } from 'react';
import {
  Card,
  CardMedia,
  CardContent,
  Typography,
  Box,
  styled,
  Tooltip,
  IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ImageIcon from '@mui/icons-material/Image';
import VideoFileIcon from '@mui/icons-material/VideoFile';
import InfoIcon from '@mui/icons-material/Info';
import { PresetMediaItem } from 'src/pages/MachineGroupDetails/UpsertPresetForm';

const MediaTile = styled(Card)(({ theme }) => ({
  position: 'relative',
  aspectRatio: '1',
  width: '200px',
  height: '200px',
  minWidth: '200px',
  minHeight: '200px',
  maxWidth: '200px',
  maxHeight: '200px',
  cursor: 'pointer',
  transition: 'transform 0.2s ease-in-out',
  flexShrink: 0,
  '&:hover': {
    transform: 'scale(1.02)',
    '& .delete-button, & .info-button': {
      opacity: 1,
    },
  },
  '&.dragging': {
    opacity: 0.5,
    transform: 'rotate(5deg)',
  },
}));

const DeleteButton = styled(IconButton)(({ theme }) => ({
  position: 'absolute',
  top: 8,
  right: 8,
  backgroundColor: 'rgba(0, 0, 0, 0.7)',
  color: 'white',
  opacity: 0,
  transition: 'opacity 0.2s ease-in-out',
  '&:hover': {
    backgroundColor: 'rgba(255, 0, 0, 0.8)',
  },
  width: 32,
  height: 32,
}));

const InfoButton = styled(IconButton)(({ theme }) => ({
  position: 'absolute',
  top: 8,
  right: 8,
  backgroundColor: 'rgba(255, 152, 0, 0.9)',
  color: 'white',
  opacity: 0,
  transition: 'opacity 0.2s ease-in-out',
  '&:hover': {
    backgroundColor: 'rgba(255, 152, 0, 1)',
  },
  width: 32,
  height: 32,
}));

const MediaPreview = styled(Box)(({ theme }) => ({
  width: '100%',
  height: '150px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: theme.palette.grey[100],
  overflow: 'hidden',
}));

interface MediaTileComponentProps {
  media: PresetMediaItem;
  onDelete: (media: PresetMediaItem) => void;
  onShowPresets: (media: PresetMediaItem) => void;
}

function MediaTileComponent({
  media,
  onDelete,
  onShowPresets,
}: MediaTileComponentProps) {
  const [fileName] = useState<string>(media.fileName);
  const [isDragging, setIsDragging] = useState(false);

  const isVideo =
    media.fullFileUrl.toLowerCase().includes('.mp4') ||
    fileName.toLowerCase().endsWith('.mp4');

  const isImage =
    media.fullFileUrl.toLowerCase().includes('.jpg') ||
    media.fullFileUrl.toLowerCase().includes('.jpeg') ||
    fileName.toLowerCase().endsWith('.jpg') ||
    fileName.toLowerCase().endsWith('.jpeg');

  const handleDragStart = (e: React.DragEvent) => {
    setIsDragging(true);
    e.dataTransfer.setData(
      'application/json',
      JSON.stringify({
        mediaId: media.id,
        mediaName: fileName,
      }),
    );
    // eslint-disable-next-line no-param-reassign
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragEnd = () => {
    setIsDragging(false);
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!media.relatedPresets || media.relatedPresets.length === 0) {
      onDelete(media);
    }
  };

  const handleShowPresets = (e: React.MouseEvent) => {
    e.stopPropagation();
    onShowPresets(media);
  };

  const renderPreview = () => {
    if (media.thumbnailUrl) {
      return (
        <CardMedia
          component="img"
          src={media.thumbnailUrl}
          alt={fileName}
          style={{ fontSize: '2rem !important' }}
          sx={{
            width: '100%',
            height: '100%',
            objectFit: 'contain',
            backgroundColor: 'grey.50',
          }}
        />
      );
    }
    if (isVideo) {
      return <VideoFileIcon sx={{ fontSize: 48, color: 'grey.500' }} />;
    }
    if (isImage) {
      return <ImageIcon sx={{ fontSize: 48, color: 'grey.500' }} />;
    }
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <ImageIcon sx={{ fontSize: 48, color: 'grey.500' }} />
        <Typography variant="caption" color="text.secondary">
          No preview
        </Typography>
      </Box>
    );
  };

  return (
    <Tooltip title={fileName} arrow>
      <MediaTile
        draggable
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        className={isDragging ? 'dragging' : ''}
      >
        {media.relatedPresets && media.relatedPresets.length > 0 ? (
          <InfoButton
            className="info-button"
            onClick={handleShowPresets}
            size="small"
          >
            <InfoIcon fontSize="small" />
          </InfoButton>
        ) : (
          <DeleteButton
            className="delete-button"
            onClick={handleDelete}
            size="small"
          >
            <CloseIcon fontSize="small" />
          </DeleteButton>
        )}

        <MediaPreview>{renderPreview()}</MediaPreview>

        <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
          <Typography
            variant="body2"
            sx={{
              fontSize: '1.2rem',
              fontWeight: 500,
              textAlign: 'center',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {fileName}
          </Typography>
          {media.relatedPresets && media.relatedPresets.length > 0 && (
            <Typography
              variant="caption"
              sx={{
                fontSize: '1.3rem',
                fontWeight: 700,
                color: 'warning.main',
                textAlign: 'center',
                display: 'block',
                mt: 0.5,
              }}
            >
              In Use
            </Typography>
          )}
        </CardContent>
      </MediaTile>
    </Tooltip>
  );
}

export default MediaTileComponent;
